import { divideArrayIntoChunks } from '@pkg/basic'
import { inArray } from 'drizzle-orm'
import { z } from 'zod'
import { sqliteMaxHostParameterCount } from '../../constants'
import {
  assistantProposalTable,
  assistantRejectedFingerprintTable,
} from '../../schema'
import type { DatabaseWithTransaction, TransactionDatabase } from '../../types'
import { parseAssistantJsonObject } from './assistantWriteUtils'
import {
  type AssistantProposalRejection,
  assistantProposalRejectionSchema,
} from './rejectAssistantProposal'

/** group拒否の1提案分を検証するschema。 */
const groupItemSchema = z
  .object({
    proposalId: z.string().min(1),
    rejection: assistantProposalRejectionSchema,
  })
  .strict()

/** group内proposal IDの重複も拒否する入力schema。 */
const groupInputSchema = z
  .array(groupItemSchema)
  .superRefine((items, context) => {
    const proposalIds = new Set<string>()
    for (const item of items) {
      if (proposalIds.has(item.proposalId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'assistant rejection proposal id must be unique',
        })
      }
      proposalIds.add(item.proposalId)
    }
  })

export type AssistantProposalGroupRejection = {
  readonly proposalId: string
  readonly rejection: AssistantProposalRejection
}

/**
 * 同一rule groupの拒否意思を1 transactionで処理する。
 * @param params DBとproposalごとのbrowser再検証結果。
 * @returns 現在存在し、削除した提案件数。
 */
export const rejectAssistantProposalGroup = async ({
  database: { transaction },
  proposals,
  validate,
}: {
  readonly database: DatabaseWithTransaction
  readonly proposals: readonly AssistantProposalGroupRejection[]
  readonly validate?: (parameters: {
    readonly database: TransactionDatabase
  }) => Promise<readonly AssistantProposalGroupRejection[]>
}): Promise<number> => {
  let parsed = groupInputSchema.parse(proposals)

  let existingProposalIds: readonly string[] = []
  let fingerprintRows: readonly (typeof assistantRejectedFingerprintTable.$inferInsert)[] =
    []

  await transaction([
    (tx) => [
      (async () => {
        parsed = groupInputSchema.parse(
          (await validate?.({ database: tx })) ?? parsed,
        )
        for (const item of parsed) {
          if (item.rejection.status === 'valid') {
            parseAssistantJsonObject({
              json: item.rejection.fingerprint.revalidationPayloadJson,
              label: 'assistant rejection revalidation payload',
            })
          }
        }
        const requestedIds = parsed.map((item) => item.proposalId)
        const proposalRows = (
          await Promise.all(
            divideArrayIntoChunks(
              requestedIds,
              sqliteMaxHostParameterCount,
            ).map((chunk) =>
              tx
                .select({
                  id: assistantProposalTable.id,
                  ruleKey: assistantProposalTable.ruleKey,
                  ruleVersion: assistantProposalTable.ruleVersion,
                })
                .from(assistantProposalTable)
                .where(inArray(assistantProposalTable.id, [...chunk])),
            ),
          )
        ).flat()
        const proposalById = new Map(
          proposalRows.map((proposal) => [proposal.id, proposal]),
        )
        if (
          new Set(proposalRows.map((proposal) => proposal.ruleKey)).size > 1
        ) {
          throw new Error('assistant rejection group must use one rule')
        }
        const nextFingerprintRows: (typeof assistantRejectedFingerprintTable.$inferInsert)[] =
          []
        for (const item of parsed) {
          const proposal = proposalById.get(item.proposalId)
          if (proposal === undefined) {
            continue
          }
          if (
            item.rejection.status === 'valid' &&
            (item.rejection.fingerprint.ruleKey !== proposal.ruleKey ||
              item.rejection.fingerprint.ruleVersion !== proposal.ruleVersion)
          ) {
            throw new Error('assistant rejection rule does not match proposal')
          }
          if (item.rejection.status === 'valid') {
            nextFingerprintRows.push({
              ...item.rejection.fingerprint,
              createdAt: new Date(),
            })
          }
        }
        existingProposalIds = proposalRows.map((proposal) => proposal.id)
        fingerprintRows = nextFingerprintRows
      })(),
    ],
    (tx) =>
      divideArrayIntoChunks(
        fingerprintRows,
        Math.max(1, Math.floor(sqliteMaxHostParameterCount / 5)),
      ).map((chunk) =>
        tx
          .insert(assistantRejectedFingerprintTable)
          .values([...chunk])
          .onConflictDoNothing(),
      ),
    (tx) =>
      divideArrayIntoChunks(
        existingProposalIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .delete(assistantProposalTable)
          .where(inArray(assistantProposalTable.id, [...chunk])),
      ),
  ])

  return existingProposalIds.length
}
